import { i18n } from '@booking-portal/config/i18n-config';
import type {
  IAdvertisedDiscount,
  IBookingPlan,
  IPeriod,
} from '@booking-portal/types';
import { useLocalFormatter } from '@booking-portal/utils/format-utils';
import type { LocaleMessage } from '@intlify/core-base';
import { useI18n, type VueMessageType } from 'vue-i18n';

export const t = (key: string, values?: unknown) => {
  return i18n.global.t(key, values as Record<string, unknown>);
};

export function useTranslationUtils() {
  const { t, locale, setLocaleMessage, getLocaleMessage } = useI18n();
  const { formatPercentage, formatCurrency, formatCompactCurrency } =
    useLocalFormatter();

  return {
    addTranslations(translations: LocaleMessage<VueMessageType>) {
      const existingMessages = getLocaleMessage(locale.value);
      setLocaleMessage(locale.value, {
        ...existingMessages,
        ...translations,
      });
    },

    getTranslatedPeriod(period: IPeriod) {
      if (period === 'weekly') {
        return t('periods.perWeek');
      }
      if (period === 'monthly') {
        return t('periods.perMonth');
      }
      return '';
    },

    getTranslatedBillingPeriod(data: Pick<IBookingPlan, 'period' | 'amount'>) {
      if (data?.period === 'weekly') {
        return t('periods.weeks', data?.amount);
      }
      if (data?.period === 'monthly') {
        return t('periods.months', data?.amount);
      }

      return '';
    },

    getTranslatedPeriodDuration(
      data?: Pick<IBookingPlan, 'period' | 'amount'> | null | undefined
    ) {
      if (!data) {
        return '';
      }

      if (data?.period === 'weekly') {
        return t('periods.everyWeeks', data?.amount);
      }
      if (data?.period === 'monthly') {
        return t('periods.everyMonths', data?.amount);
      }

      return '';
    },

    getTranslatedDiscountCode(
      discount?: IAdvertisedDiscount | null | undefined
    ) {
      if (discount == null) {
        return null;
      }

      let value = '';

      const translations = {
        forever: t('discount.offForever'),
        multiple_month: t('discount.offMonths', discount.durationMonth || 0),
        once: t('discount.offOnce'),
      };

      if (discount.discountType === 'percentage') {
        value = formatPercentage(discount.discountValue);
      } else {
        value = formatCurrency(discount.discountValue);
      }

      const parts = [value, translations[discount.duration]];

      return parts.join(' ');
    },

    getTranslatedDiscountTerms(
      discount: IAdvertisedDiscount | null | undefined,
      fallbackPercentage: number
    ) {
      if (discount == null) {
        return t('discount.advertising.genericTerms', {
          discount: formatPercentage(-fallbackPercentage),
        });
      }

      const value =
        discount.discountType === 'percentage'
          ? formatPercentage(-discount.discountValue)
          : formatCompactCurrency(-discount.discountValue);

      // A `once` discount is a Stripe once-coupon: it reduces the entire
      // first invoice, regardless of billing period length.
      if (discount.duration === 'once') {
        return t('discount.advertising.onceTerms', { discount: value });
      }

      if (discount.duration === 'multiple_month') {
        const duration = discount.durationMonth ?? 0;
        return t(
          'discount.advertising.monthsTerms',
          { discount: value, duration },
          duration
        );
      }

      return t('discount.advertising.foreverTerms', { discount: value });
    },
  };
}
